/**
 * @fileoverview StorageService handles all localStorage operations.
 * Abstracts storage access and provides typed APIs for game data.
 * This is the SINGLE source of truth for all persistent storage in the app.
 */

import { IStorageAdapter, LocalStorageAdapter } from './IStorageAdapter.js';
import { MatchingPair } from '../utilities/GameUtils.js';
import { BaseTheme } from '../themes/abstract/BaseTheme.js';

/**
 * Progress data structure.
 */
export interface Progress {
    completedPairs: number[];
    completedCount: number;
    timestamp?: number;
}

/**
 * Matching pairs configuration structure.
 */
export interface MatchingPairsConfig {
    pairs: MatchingPair[];
    numPairs: number;
    themeId: string;
    timestamp: number;
}

/**
 * Story progress data structure.
 */
export interface StoryProgressData {
    storyId: string;
    currentPage: number;
    completed: boolean;
    lastRead: number;
}

/**
 * StorageService manages all persistent storage operations.
 * Uses an adapter pattern to allow different storage backends.
 * Singleton — use StorageService.getInstance() or the static convenience methods.
 */
export class StorageService {
    private adapter: IStorageAdapter;
    private static instance: StorageService;
    
    // Storage keys — centralised here so no key strings are scattered across the codebase
    private static readonly MATCHING_PAIRS_KEY = 'toddlerGame_matchingPairs';
    private static readonly PROGRESS_KEY = 'toddlerGame_progress';
    private static readonly STORY_PROGRESS_KEY = 'toddlerGame_storyProgress';
    private static readonly STORY_PURCHASES_KEY = 'toddlerGame_storyPurchases';
    
    /**
     * @param adapter - Storage adapter implementation (defaults to LocalStorageAdapter)
     */
    constructor(adapter: IStorageAdapter = new LocalStorageAdapter()) {
        this.adapter = adapter;
    }

    /**
     * Get the singleton instance.
     */
    static getInstance(): StorageService {
        if (!StorageService.instance) {
            StorageService.instance = new StorageService();
        }
        return StorageService.instance;
    }

    // ─── Matching Pairs ────────────────────────────────────────────────
    
    /**
     * Save matching pairs configuration to storage.
     */
    saveMatchingPairs(pairs: MatchingPair[], theme: BaseTheme): void {
        const config: MatchingPairsConfig = {
            pairs: pairs,
            numPairs: theme.numPairs,
            themeId: theme.id,
            timestamp: Date.now()
        };
        this.adapter.set(StorageService.MATCHING_PAIRS_KEY, config);
    }
    
    /**
     * Load matching pairs configuration from storage.
     * @param themeId - Current theme ID
     * @param expectedPairs - Expected number of pairs
     * @returns Saved matching pairs or null if not found/invalid
     */
    loadMatchingPairs(themeId: string, expectedPairs: number): MatchingPair[] | null {
        const config = this.adapter.get(StorageService.MATCHING_PAIRS_KEY) as MatchingPairsConfig | null;
        
        if (!config) {
            return null;
        }
        
        // Validate theme ID matches
        if (config.themeId !== themeId) {
            console.log(`Saved configuration is for theme '${config.themeId}' but current theme is '${themeId}', generating new one`);
            return null;
        }
        
        // Validate number of pairs matches
        if (config.numPairs !== expectedPairs) {
            console.log(`Saved configuration has ${config.numPairs} pairs but theme needs ${expectedPairs}, generating new one`);
            return null;
        }
        
        // Load progress and apply completion status
        const progress = this.loadProgress();
        config.pairs.forEach((pair, index) => {
            pair.completed = progress.completedPairs.includes(index);
        });
        
        return config.pairs;
    }

    // ─── Game Progress ─────────────────────────────────────────────────
    
    /**
     * Save game progress (completed pairs) to storage.
     */
    saveProgress(pairs: MatchingPair[]): void {
        const completedPairs = pairs
            .map((pair, index) => pair.completed ? index : null)
            .filter(index => index !== null) as number[];
        
        const progress: Progress = {
            completedPairs: completedPairs,
            completedCount: completedPairs.length,
            timestamp: Date.now()
        };
        
        this.adapter.set(StorageService.PROGRESS_KEY, progress);
    }
    
    /**
     * Load game progress from storage.
     */
    loadProgress(): Progress {
        const progress = this.adapter.get(StorageService.PROGRESS_KEY) as Progress | null;
        
        if (!progress) {
            return { completedPairs: [], completedCount: 0 };
        }
        
        return {
            completedPairs: progress.completedPairs || [],
            completedCount: progress.completedCount || 0,
            timestamp: progress.timestamp
        };
    }
    
    /**
     * Clear progress data from storage.
     */
    clearProgress(): void {
        this.adapter.remove(StorageService.PROGRESS_KEY);
        console.log('Cleared saved progress');
    }
    
    /**
     * Clear matching pairs configuration and progress.
     * Forces generation of new random configuration.
     */
    clearSavedConfiguration(): void {
        this.adapter.remove(StorageService.MATCHING_PAIRS_KEY);
        this.clearProgress();
        console.log('Cleared saved matching pairs configuration and progress');
    }

    // ─── Story Progress ────────────────────────────────────────────────

    /**
     * Load all story progress entries from storage.
     */
    loadAllStoryProgress(): { [storyId: string]: StoryProgressData } {
        return (this.adapter.get(StorageService.STORY_PROGRESS_KEY) as { [storyId: string]: StoryProgressData }) || {};
    }

    /**
     * Load progress for a single story.
     */
    loadStoryProgress(storyId: string): StoryProgressData | null {
        const all = this.loadAllStoryProgress();
        return all[storyId] || null;
    }

    /**
     * Save page progress for a story.
     */
    saveStoryProgress(storyId: string, page: number): void {
        const all = this.loadAllStoryProgress();
        all[storyId] = {
            storyId,
            currentPage: page,
            completed: false,
            lastRead: Date.now()
        };
        this.adapter.set(StorageService.STORY_PROGRESS_KEY, all);
    }

    /**
     * Mark a story as completed.
     */
    markStoryCompleted(storyId: string): void {
        const all = this.loadAllStoryProgress();
        if (all[storyId]) {
            all[storyId].completed = true;
            all[storyId].lastRead = Date.now();
            this.adapter.set(StorageService.STORY_PROGRESS_KEY, all);
        }
    }

    // ─── Story Purchases ───────────────────────────────────────────────

    /**
     * Get list of purchased story IDs.
     */
    getPurchasedStories(): string[] {
        return (this.adapter.get(StorageService.STORY_PURCHASES_KEY) as string[]) || [];
    }

    /**
     * Check if a story has been purchased.
     */
    isStoryPurchased(storyId: string): boolean {
        return this.getPurchasedStories().includes(storyId);
    }

    /**
     * Mark a story as purchased.
     */
    markStoryPurchased(storyId: string): void {
        const purchased = this.getPurchasedStories();
        if (!purchased.includes(storyId)) {
            purchased.push(storyId);
            this.adapter.set(StorageService.STORY_PURCHASES_KEY, purchased);
            console.log(`Story ${storyId} marked as purchased`);
        }
    }

    /**
     * Clear all story data (progress and purchases).
     */
    clearStoryData(): void {
        this.adapter.remove(StorageService.STORY_PROGRESS_KEY);
        this.adapter.remove(StorageService.STORY_PURCHASES_KEY);
        console.log('All story data reset');
    }
    
    /**
     * Clear all game data from storage.
     */
    clearAll(): void {
        this.clearSavedConfiguration();
        this.clearStoryData();
        console.log('Cleared all game data');
    }
}
